import * as React from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import arrayMutators from 'final-form-arrays';
import { FORM_ERROR, type FormApi } from 'final-form';
import { Form, useField, useForm, useFormState } from 'react-final-form';
import { DragDropContext, type DropResult } from '@hello-pangea/dnd';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../services';
import ZuulLoader from '../../../components/ZuulLoader/ZuulLoader';
import ActiveStatusModal from '../../../components/ActiveStatusModal/ActiveStatusModal';
import AuditModalButton from '../../../components/AuditModal/AuditModal';
import CommandK from '../../../components/CommandK/CommandK/CommandK';
import DeleteModal from '../../../components/DeleteModal/DeleteModal';
import { TestLinkButton } from '../../TestLinkBuilder/TestLinkBuilderDialog';
import { UnifiedFormCriteriaPanel } from '../../../components/Criteria/CriteriaPanel/UnifiedFormCriteriaPanel';
import { formatDate } from '../../../utils/conversionFunctions';
import { getPageTitle } from '../../../utils/pageTitles';
import {
  BuilderHeader,
  ConfigPanel,
  DESTINATION,
  EditorSwitch,
  InlineNameField,
  insertVisible,
  LIBRARY_DROPPABLE_ID,
  moveStep,
  moveToEdge,
  moveVisible,
  OFFER_BLOCK,
  OfferLibrary,
  OfferPopover,
  OfferStack,
  STACK_DROPPABLE_ID,
  StackBuilderLayout,
  displayId,
  entityLinkFor,
  typeLabel,
  useUnsavedChangesGuard,
  VariationChips,
  variationsOf,
  type StackEntry,
} from '../../../features/stackBuilder';
import {
  buildCreatePublish,
  buildSequenceView,
  buildUpdatePublish,
  createStackItem,
  emptySequenceValues,
  loadSequenceView,
  positionSpan,
  type LoadedSequence,
  type Offer,
  type SaveContext,
  type StackItem,
} from './sequenceModel';
import {
  useBuilderOffers,
  useContainersList,
  useLayoutsList,
  useOfferTypesByName,
  useOfferVariationsMap,
  useSequencePublished,
  useSequenceView,
  useSitesList,
} from './queries';
import SequenceSettings from './SequenceSettings';
import SequenceOfferDetails from './SequenceOfferDetails';
import validate from './validate';

/* eslint-disable @typescript-eslint/no-explicit-any -- API entities */
const zuulUser = () => {
  try {
    return JSON.parse(localStorage.getItem('userData') || 'null');
  } catch {
    return null;
  }
};

/** The backend's message for a failed save, or the classic editor's wording for the known cases. */
const describeSaveError = (error: any) => {
  if (error?.response?.status === 504) {
    return 'This sequence has a large number of offers, so updating its templates takes longer than usual. Check for your template changes in about five minutes.';
  }
  return (
    error?.response?.data?.message ||
    'The sequence could not be published. Try again, or contact a developer if it keeps failing.'
  );
};

const memberOfferIds = (offer: Offer | undefined) =>
  (offer?.offerBlock?.offerBlockOffers ?? [])
    .map((member) => member?.offer?.id)
    .filter((id): id is number => id != null);

/** How many times an offer is already placed, directly or inside a block — the classic duplicate check. */
function countPlacements(stack: StackItem[], offer: Offer, offersById: Map<number, Offer>) {
  const draggedMembers = memberOfferIds(offer);
  return stack.reduce((count, item) => {
    if (draggedMembers.length)
      return count + (item.offer.id === offer.id || draggedMembers.includes(item.offer.id) ? 1 : 0);
    if (item.offer.id === offer.id) return count + 1;
    if (item.offer.offerType?.name === OFFER_BLOCK) {
      const block = offersById.get(item.offer.id) ?? item.offer;
      if (memberOfferIds(block).includes(offer.id)) return count + 1;
    }
    return count;
  }, 0);
}

/**
 * ZET-5084: shown while a sequence has more than one Destination, in place of the modal that
 * interrupted every add. Only one Destination is ever served, so the rest are fallbacks; the
 * hover explains that and lists them in serve order.
 */
function MultipleDestinationsBanner({ destinations }: { destinations: Offer[] }) {
  if (destinations.length < 2) return null;
  const explanation = (
    <Stack spacing={1} sx={{ p: 0.5 }}>
      <Typography variant='subtitle2'>Only one Destination is served</Typography>
      <Typography variant='body2'>
        Zuul sends the user to the first Destination that passes its criteria, and the sequence ends there.
      </Typography>
      <Typography variant='body2'>
        The others are fallbacks. They are only used if every Destination above them is skipped (criteria failed or
        waterfall exhausted).
      </Typography>
      <Typography variant='overline' sx={{ lineHeight: 1.5 }}>
        Serve order
      </Typography>
      <Stack component='ol' spacing={0.75} sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {destinations.map((offer, index) => {
          const first = index === 0;
          return (
            <Stack
              component='li'
              key={`${offer.id}-${index}`}
              direction='row'
              spacing={1}
              sx={{ alignItems: 'center', typography: 'body2' }}
            >
              <Box
                sx={{
                  flex: 'none',
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  bgcolor: first ? 'warning.main' : 'action.selected',
                  color: first ? 'warning.contrastText' : 'text.primary',
                }}
              >
                {index + 1}
              </Box>
              <Typography
                component='span'
                variant='body2'
                noWrap
                title={offer.name}
                sx={{ flex: '1 1 auto', minWidth: 0 }}
              >
                {offer.name}
              </Typography>
              <Box
                component='span'
                sx={{ flex: 'none', fontWeight: 600, color: first ? 'warning.main' : 'text.secondary' }}
              >
                {first ? 'Tried first' : 'Fallback'}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
  return (
    <Alert
      severity='warning'
      sx={{ mt: 1, py: 0, alignItems: 'center' }}
      action={
        <Tooltip
          title={explanation}
          arrow
          describeChild
          disableInteractive
          placement='bottom-end'
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 360,
                p: 1.5,
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: 1,
                borderColor: 'warning.dark',
                boxShadow: 8,
              },
            },
            arrow: { sx: { color: 'background.paper', '&::before': { border: 1, borderColor: 'warning.dark' } } },
          }}
        >
          <Button
            color='inherit'
            size='small'
            sx={{ textDecoration: 'underline', textUnderlineOffset: 3, whiteSpace: 'nowrap' }}
          >
            How it works
          </Button>
        </Tooltip>
      }
    >
      <strong>{`${destinations.length} Destinations`}</strong> in this sequence. Only the first eligible one is served;
      the rest are fallbacks.
    </Alert>
  );
}

type Confirm = { title: string; body: React.ReactNode; confirmLabel: string; resolve: (ok: boolean) => void };

function Workbench({
  sequenceId,
  loaded,
  published,
  copyRef,
  activeRef,
}: {
  sequenceId: number | null;
  loaded: LoadedSequence;
  published: any;
  copyRef: React.MutableRefObject<SaveContext['copy']>;
  /** The live Active state, which the toggle saves on its own and the next publish must carry. */
  activeRef: React.MutableRefObject<boolean>;
}) {
  const form = useForm();
  const { dirty, submitting, submitFailed, submitError, hasValidationErrors, errors } = useFormState({
    subscription: {
      dirty: true,
      submitting: true,
      submitFailed: true,
      submitError: true,
      hasValidationErrors: true,
      errors: true,
    },
  });
  const { input: stackInput } = useField<StackItem[]>('stack', { subscription: { value: true } });
  const stack = React.useMemo(() => (Array.isArray(stackInput.value) ? stackInput.value : []), [stackInput.value]);
  const { input: siteInput } = useField<any>('site', { subscription: { value: true } });
  const { input: nameInput, meta: nameMeta } = useField<string>('sequenceName', {
    subscription: { value: true, error: true, touched: true },
  });

  const { data: offers, isLoading: offersLoading } = useBuilderOffers();
  const { data: variationsMap } = useOfferVariationsMap();
  const { data: sites, isLoading: sitesLoading } = useSitesList();
  const { data: layouts, isLoading: layoutsLoading } = useLayoutsList();
  const { data: containers, isLoading: containersLoading } = useContainersList();
  const { data: offerTypesByName } = useOfferTypesByName();

  const isEditMode = sequenceId != null;
  const [isActive, setIsActive] = React.useState<boolean>(Boolean(loaded.sequence?.isActive));
  activeRef.current = isActive;
  const [popover, setPopover] = React.useState<{ key: string; anchor: HTMLElement } | null>(null);
  const [confirm, setConfirm] = React.useState<Confirm | null>(null);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [copyOpen, setCopyOpen] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const visibleKeysRef = React.useRef<string[]>([]);
  const listedRef = React.useRef<Offer[]>([]);
  const onListedChange = React.useCallback((listed: Offer[]) => {
    listedRef.current = listed;
  }, []);

  const guard = useUnsavedChangesGuard(dirty && !submitting);

  // The library's rows are the freshest copy of each offer (name, status, block members).
  const offersById = React.useMemo(() => new Map((offers ?? []).map((offer) => [offer.id, offer])), [offers]);
  const fresh = React.useCallback((item: StackItem) => offersById.get(item.offer.id) ?? item.offer, [offersById]);

  const setStack = (next: StackItem[]) => form.change('stack', next);

  const ask = (title: string, body: React.ReactNode, confirmLabel: string) =>
    new Promise<boolean>((resolve) => setConfirm({ title, body, confirmLabel, resolve }));

  const addOffer = async (offer: Offer, visibleIndex?: number) => {
    const current = (form.getState().values.stack ?? []) as StackItem[];
    const placed = countPlacements(current, offer, offersById);
    if (placed > 0) {
      const subject = memberOfferIds(offer).length ? 'At least one offer in this block' : `${offer.id}: ${offer.name}`;
      const ok = await ask(
        'Add it again?',
        `${subject} is already in this sequence ${placed === 1 ? 'once' : `${placed} times`}.`,
        'Add again'
      );
      if (!ok) return;
    }
    const item = createStackItem(offer);
    const latest = (form.getState().values.stack ?? []) as StackItem[];
    const next =
      visibleIndex === undefined
        ? [...latest, item]
        : insertVisible(latest, visibleKeysRef.current, (row) => row.key, item, visibleIndex);
    setStack(next);
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination || destination.droppableId !== STACK_DROPPABLE_ID) return;
    if (source.droppableId === LIBRARY_DROPPABLE_ID) {
      const offer = listedRef.current[source.index];
      if (offer) void addOffer(offer, destination.index);
      return;
    }
    setStack(moveVisible(stack, visibleKeysRef.current, (row) => row.key, source.index, destination.index));
  };

  const entries: StackEntry[] = React.useMemo(
    () =>
      stack.map((item) => {
        const offer = fresh(item);
        const placements = stack.filter((other) => other.offer.id === item.offer.id).length;
        const members = (offer.offerBlock?.offerBlockOffers ?? []).map((member) => ({
          id: member?.offer?.id as number,
          name: member?.offer?.name ?? '',
        }));
        const overrides = [item.layoutOverride && 'Layout', item.containerOverride && 'Container'].filter(Boolean);
        return {
          key: item.key,
          offer,
          span: positionSpan(offer),
          members: offer.offerType?.name === OFFER_BLOCK ? members : undefined,
          hasOverride: overrides.length > 0,
          badges: (
            <>
              <VariationChips variations={variationsOf(offer, variationsMap)} />
              {overrides.length > 0 && (
                <Chip
                  size='small'
                  color='info'
                  variant='outlined'
                  label={`${overrides.join(' and ')} override`}
                  sx={{ height: 18, fontSize: '0.6875rem' }}
                />
              )}
              {placements > 1 && (
                <Chip
                  size='small'
                  color='warning'
                  variant='outlined'
                  label={`Placed ${placements} times`}
                  sx={{ height: 18, fontSize: '0.6875rem' }}
                />
              )}
            </>
          ),
        };
      }),
    [stack, fresh, variationsMap]
  );

  const popoverIndex = popover ? stack.findIndex((item) => item.key === popover.key) : -1;
  const popoverItem = popoverIndex >= 0 ? stack[popoverIndex] : undefined;
  const popoverStart = React.useMemo(() => {
    if (popoverIndex < 0) return 0;
    return stack.slice(0, popoverIndex).reduce((sum, item) => sum + positionSpan(fresh(item)), 1);
  }, [stack, popoverIndex, fresh]);

  const removeKey = (key: string) => {
    setStack(stack.filter((item) => item.key !== key));
    if (popover?.key === key) setPopover(null);
  };

  const toggleActive = (next: boolean) => {
    if (!isEditMode) return;
    api.put(`sequences/active/${sequenceId}`, { id: sequenceId, isActive: next }).then(() => setIsActive(next));
  };

  const publish = () => {
    if (hasValidationErrors) {
      void form.submit(); // marks every field touched and shows what to fix
      return;
    }
    setPublishOpen(true);
  };

  const errorCount = Object.keys(errors ?? {}).length;
  const meta = (
    <>
      {submitError ? (
        <Typography variant='caption' color='error'>
          {submitError}
        </Typography>
      ) : submitFailed && hasValidationErrors ? (
        <Typography variant='caption' color='error'>
          {`Fix ${errorCount === 1 ? 'the highlighted field' : `${errorCount} highlighted fields`} before publishing.`}
        </Typography>
      ) : published?.updatedAt ? (
        <Typography variant='caption' color='text.secondary' noWrap>
          {`Published ${formatDate(published.updatedAt)}${published?.zuulUser?.realname ? ` by ${published.zuulUser.realname}` : ''}`}
        </Typography>
      ) : (
        <Typography variant='caption' color='text.secondary'>
          Not published yet
        </Typography>
      )}
      {dirty && (
        <Typography variant='caption' color='warning.main'>
          Unpublished changes
        </Typography>
      )}
    </>
  );

  return (
    <>
      <Helmet>
        <title>{getPageTitle(window.location.pathname, nameInput.value)}</title>
      </Helmet>
      <DragDropContext onDragEnd={onDragEnd}>
        <StackBuilderLayout
          header={
            <BuilderHeader
              isActive={isActive}
              backTo='/campaigns/sequences'
              backLabel='Sequences'
              name={
                <InlineNameField
                  value={nameInput.value}
                  onChange={nameInput.onChange}
                  onBlur={() => nameInput.onBlur()}
                  label='Sequence name'
                  placeholder='Name this sequence'
                  error={nameMeta.error}
                  showError={Boolean(nameMeta.touched || submitFailed)}
                />
              }
              identifier={isEditMode ? `ID ${sequenceId}` : 'New sequence'}
              status={
                <ActiveStatusModal
                  framed
                  isActive={isActive}
                  modalType='Sequence'
                  content={{ name: nameInput.value }}
                  onClickSubmit={toggleActive}
                  disabled={!isEditMode}
                />
              }
              actions={
                <>
                  <EditorSwitch screen='sequence' confirmLoss={dirty} />
                  {isEditMode && (
                    <TestLinkButton
                      label='Build a test link for this sequence'
                      prefill={{ siteId: siteInput.value?.id ?? null, entities: { sequence_id: sequenceId } }}
                    />
                  )}
                  <AuditModalButton />
                  {isEditMode && (
                    <Button size='small' variant='outlined' onClick={() => setCopyOpen(true)} disabled={submitting}>
                      Copy
                    </Button>
                  )}
                  <Button size='small' variant='contained' onClick={publish} disabled={submitting}>
                    {submitting ? 'Publishing' : 'Publish'}
                  </Button>
                  {isEditMode && (
                    <DeleteModal
                      outlined
                      modalType=' Sequence'
                      id={sequenceId}
                      entity='sequences'
                      content={{ name: nameInput.value }}
                    />
                  )}
                  <CommandK hide />
                </>
              }
              meta={meta}
            />
          }
          banner={
            notice ? (
              <Alert severity='warning' onClose={() => setNotice(null)} sx={{ mx: 1.5, mt: 1.5 }}>
                {notice}
              </Alert>
            ) : undefined
          }
          library={
            <OfferLibrary
              offers={offers ?? []}
              loading={offersLoading}
              onAdd={(offer) => void addOffer(offer)}
              onListedChange={onListedChange}
            />
          }
          stack={
            <OfferStack
              title='Sequence'
              entries={entries}
              notice={
                <MultipleDestinationsBanner
                  destinations={stack.filter((item) => fresh(item).offerType?.name === DESTINATION).map(fresh)}
                />
              }
              visibleKeysRef={visibleKeysRef}
              selectedKey={popover?.key}
              onOpen={(key, anchor) => setPopover({ key, anchor })}
              onRemove={removeKey}
              onMoveToEdge={(key, edge) =>
                setStack(
                  moveToEdge(
                    stack,
                    stack.findIndex((item) => item.key === key),
                    edge
                  )
                )
              }
              onMoveStep={(key, delta) =>
                setStack(moveStep(stack, visibleKeysRef.current, (row) => row.key, key, delta))
              }
              emptyText='Drag offers here from the library, or press + on one, to build the order.'
              removeLabel='Remove from sequence'
            />
          }
          config={
            <ConfigPanel
              label='Sequence configuration'
              tabs={[
                {
                  value: 'settings',
                  label: 'Settings',
                  content: (
                    <SequenceSettings
                      isEditMode={isEditMode}
                      sites={sites}
                      sitesLoading={sitesLoading}
                      layouts={layouts}
                      containers={containers}
                      offerTypesByName={offerTypesByName}
                      templatesLoading={layoutsLoading || containersLoading}
                    />
                  ),
                },
                {
                  value: 'criteria',
                  label: 'Criteria',
                  hasError: Boolean(submitFailed && errors?.criteria),
                  // The same shared panel every other criteria screen uses, unchanged.
                  content: <UnifiedFormCriteriaPanel name='criteria' sourceType='sequence' />,
                },
              ]}
            />
          }
        />
      </DragDropContext>

      {popoverItem && (
        <OfferPopover
          anchorEl={popover?.anchor ?? null}
          onClose={() => setPopover(null)}
          title={fresh(popoverItem).name}
          meta={
            <>
              <Typography variant='caption'>{`Position ${popoverStart}`}</Typography>
              <Typography variant='caption'>{`${displayId(fresh(popoverItem)).label} ${displayId(fresh(popoverItem)).id}`}</Typography>
              {popoverItem.offersSequence?.id && (
                <Typography variant='caption'>{`Offers-sequence ${popoverItem.offersSequence.id}`}</Typography>
              )}
              <Chip size='small' label={typeLabel(fresh(popoverItem))} sx={{ height: 18, fontSize: '0.6875rem' }} />
              {!fresh(popoverItem).isActive && (
                <Chip
                  size='small'
                  color='error'
                  variant='outlined'
                  label='Inactive'
                  sx={{ height: 18, fontSize: '0.6875rem' }}
                />
              )}
            </>
          }
          openLink={(() => {
            const offer = fresh(popoverItem);
            const { to } = entityLinkFor(offer);
            const type = offer.offerType?.name;
            return {
              to,
              label:
                type === DESTINATION ? 'Open offer action' : type === OFFER_BLOCK ? 'Open offer block' : 'Open offer',
            };
          })()}
          onRemove={() => removeKey(popoverItem.key)}
          removeLabel='Remove from sequence'
        >
          <SequenceOfferDetails
            item={{ ...popoverItem, offer: fresh(popoverItem) }}
            index={popoverIndex}
            layouts={layouts}
            containers={containers}
            offerTypesByName={offerTypesByName}
            variations={variationsOf(fresh(popoverItem), variationsMap)}
          />
        </OfferPopover>
      )}

      <Dialog
        open={Boolean(confirm)}
        onClose={() => {
          confirm?.resolve(false);
          setConfirm(null);
        }}
      >
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>
          {typeof confirm?.body === 'string' ? <DialogContentText>{confirm.body}</DialogContentText> : confirm?.body}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              confirm?.resolve(false);
              setConfirm(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              confirm?.resolve(true);
              setConfirm(null);
            }}
          >
            {confirm?.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={publishOpen} onClose={() => setPublishOpen(false)}>
        <DialogTitle>Publish this sequence?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The published version is replaced with what you see here, and goes live straight away.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={() => {
              setPublishOpen(false);
              copyRef.current = null;
              void form.submit();
            }}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      <CopyDialog
        open={copyOpen}
        sites={sites}
        defaultName={`${nameInput.value} copy`}
        onClose={() => setCopyOpen(false)}
        onCopy={(copy) => {
          setCopyOpen(false);
          copyRef.current = copy;
          void Promise.resolve(form.submit()).finally(() => {
            copyRef.current = null;
          });
        }}
      />
      {guard}
    </>
  );
}

function CopyDialog({
  open,
  sites,
  defaultName,
  onClose,
  onCopy,
}: {
  open: boolean;
  sites: any[] | undefined;
  defaultName: string;
  onClose: () => void;
  onCopy: (copy: { name: string; site: { id: number; domain: string } }) => void;
}) {
  const [name, setName] = React.useState(defaultName);
  const [site, setSite] = React.useState<any>(null);
  const [tried, setTried] = React.useState(false);
  React.useEffect(() => {
    if (open) {
      setName(defaultName);
      setSite(null);
      setTried(false);
    }
  }, [open, defaultName]);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='xs'>
      <DialogTitle>Copy this sequence</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The copy is a new, inactive sequence with this one's offers and settings, including changes you have not
          published.
        </DialogContentText>
        <Stack spacing={2}>
          <TextField
            label='Name of the copy'
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={tried && !name.trim()}
            helperText={tried && !name.trim() ? 'Name the copy.' : undefined}
            fullWidth
            size='small'
            autoFocus
          />
          <Autocomplete
            options={sites ?? []}
            value={site}
            onChange={(_event, next) => setSite(next)}
            getOptionLabel={(option: any) => option?.domain ?? ''}
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Site'
                size='small'
                error={tried && !site}
                helperText={tried && !site ? 'Pick a site for the copy.' : undefined}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant='contained'
          onClick={() => {
            setTried(true);
            if (name.trim() && site) onCopy({ name: name.trim(), site: { id: site.id, domain: site.domain } });
          }}
        >
          Copy
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The stack builder for sequences: an offer library, the sequence as an ordered stack, and its
 * settings and criteria. Saves exactly what the classic editor saves (see sequenceModel).
 */
export default function SequenceBuilder() {
  const params = useParams();
  const navigate = useNavigate();
  const sequenceId = params.sequenceId ? parseInt(params.sequenceId, 10) : null;
  const { data: sequenceView, isLoading, isError } = useSequenceView(sequenceId);
  const { data: published } = useSequencePublished(sequenceId);
  const { data: offerTypesByName } = useOfferTypesByName();
  const copyRef = React.useRef<SaveContext['copy']>(null);
  const activeRef = React.useRef<boolean>(false);

  const loaded = React.useMemo<LoadedSequence | null>(() => {
    if (sequenceId == null)
      return { values: emptySequenceValues(), sdtsByType: {}, returnUrlOfferSequence: null, sequence: {} };
    return sequenceView ? loadSequenceView(sequenceView) : null;
  }, [sequenceId, sequenceView]);

  if (isError) {
    return (
      <Alert severity='error' sx={{ m: 2 }}>
        This sequence could not be loaded. Reload the page, or switch to the classic editor from the sequence list.
      </Alert>
    );
  }
  if ((sequenceId != null && isLoading) || !loaded) return <ZuulLoader text='Loading sequence' />;

  const onSubmit = async (values: Record<string, any>, formApi: FormApi) => {
    const copy = copyRef.current;
    const ctx: SaveContext = {
      sequenceId,
      copy,
      isActive: activeRef.current,
      offerTypesByName: offerTypesByName ?? {},
      sdtsByType: loaded.sdtsByType,
      returnUrlOfferSequence: loaded.returnUrlOfferSequence,
      sequence: loaded.sequence,
    };
    try {
      const sequenceViewPayload = buildSequenceView(values, ctx);
      if (sequenceId != null && !copy) {
        const { data: response } = await api.put(`sequences/sequenceView/${sequenceId}`, sequenceViewPayload);
        await api.put(
          `sequences/publish/${sequenceId}`,
          buildUpdatePublish(values, sequenceViewPayload, response, zuulUser())
        );
      } else {
        const { data: response } = await api.post('sequences/sequenceView', sequenceViewPayload);
        await api.post('sequences/publish', buildCreatePublish(response, zuulUser()));
      }
      formApi.reset(values); // clean, so leaving does not ask about unsaved changes
      navigate('/campaigns/sequences');
      return undefined;
    } catch (error) {
      return { [FORM_ERROR]: describeSaveError(error) };
    }
  };

  return (
    <Form
      initialValues={loaded.values}
      onSubmit={onSubmit}
      validate={validate}
      mutators={{ ...arrayMutators }}
      subscription={{}}
    >
      {() => (
        <Workbench
          sequenceId={sequenceId}
          loaded={loaded}
          published={published}
          copyRef={copyRef}
          activeRef={activeRef}
        />
      )}
    </Form>
  );
}
